import React, { forwardRef, useState, useEffect, useRef, useMemo } from "react";
import { Typography, Table, Modal } from "antd";
import HouseVisit from "../../houseVisit/index";
import MonthlyPlanningTable from "../components/MonthlyPlanningTable";
import { useIntl } from "react-intl";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import {
  setMonth,
  updateHouseVisits,
  verifyControllersVisits,
} from "../employeeHouseVisitsSlice";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import Surface from "../../../components/layouts/Surface";
import { deleteRecord } from "../../houseVisit/houseVisitSlice";
import Header from "./Header";
import FloatingSubmitButton from "../../../components/templates/FloatingSubmitButton";
import TransferHouseVisits from "./TransferHouseVisits";
import _ from "lodash";
import { useReactToPrint } from "react-to-print";
import EmployeeHouseVisitsWrapper from "./EmployeeHouseVisits.style";
import { getFullName } from "../../../components/util/helpers";
import PrintVersion from "../../houseVisit/form/PrintVersion";
import { getHouseVisit, loadImage } from "../../../components/util/helpers";
import jsPDF from "jspdf";
import { useUserBranches } from "../../../hooks/accessControl";
import { getColumns } from "./houseVisitsUtils";
import { VList } from "virtuallist-antd";
import { StyledTable } from "./index.style";

const { Title } = Typography;

const VisitsTable = forwardRef((props, ref) => {
  const { items, loading, type, loadPage } = props;
  const dispatch = useDispatch();
  const userBranches = useUserBranches();
  let location = useLocation();
  const [modal, contextHolder] = Modal.useModal();
  let { branchId } = useParams();
  const activeEmployee = useSelector(
    (state) => state.employeeDetails.activeEmployee
  );
  const { scopePrefix } = useSelector((state) => state.user);
  const organization = useSelector((state) => state.organization.details);
  const componentRef = useRef();
  const houseVisitRef = useRef();

  const intl = useIntl();
  const [mode, setMode] = useState(null);
  const [visible, setVisible] = useState(false);
  const [formType, setFormType] = useState(null);
  const [tableItems, setTableItems] = useState([]);
  const [changedVisits, setChangedVisits] = useState([]);
  const [filter, setFilter] = useState(null);
  const [filteredItems, setFilteredItems] = useState([]);
  const [showLocalId, setShowLocalId] = useState(false);

  const { activeMonth } = useSelector((state) => state.employeeHouseVisits);
  const activeHouseVisit = useSelector(
    (state) => state.houseVisit.activeHouseVisit
  );

  useEffect(() => {
    setTableItems(items);
  }, [items]);

  useEffect(() => {
    switch (filter) {
      case "approved_visits":
        setFilteredItems(tableItems.filter((visit) => visit.approved));
        break;
      case "not_verified_visits":
        setFilteredItems(tableItems.filter((visit) => !visit.verified));
        break;
      case "exception_visits":
        setFilteredItems(
          tableItems.filter(
            (visit) =>
              visit.overflow_days_number > 0 ||
              visit.care_giver_not_presented_error === true ||
              visit.professional_employee_missing_error === true
          )
        );
        break;

      default:
        setFilteredItems(tableItems);
    }
  }, [tableItems, filter]);

  const onPrintAllVisits = async () => {
    const avatar_url = organization.avatar_url;
    const logo = await loadImage(avatar_url);
    let diariesPdf = new jsPDF({ orientation: "p" });
    if (filteredItems && filteredItems.length > 0) {
      let j = 0;
      for (const item of filteredItems) {
        if (j > 0) {
          diariesPdf.addPage();
          const last = diariesPdf.getNumberOfPages();
          diariesPdf.setPage(last);
        }
        j++;
        let approvalImg = null;
        let verifyImg = null;
        if (item.approval_signature) {
          approvalImg = await loadImage(item.approval_signature.url);
        }
        if (item.verification_signature) {
          verifyImg = await loadImage(item.verification_signature.url);
        }
        diariesPdf = getHouseVisit(
          diariesPdf,
          intl,
          item,
          userBranches,
          organization,
          logo,
          approvalImg,
          verifyImg
        );
      }

      return window.open(diariesPdf.output("bloburl"), "_blank");
    }
  };

  const onVerifyVisits = () => {
    const title = intl.formatMessage({
      id: "app.house_visits.verifyControllersVisitsTitle",
    });
    const content = intl.formatMessage({
      id: "app.house_visits.verifyControllersVisitsContent",
    });
    modal.confirm({
      title: title,
      icon: <ExclamationCircleOutlined />,
      content: content,
      onOk() {
        return onConfirmVerifyVisits();
      },
      onCancel() {},
    });
  };

  const onConfirmVerifyVisits = async () => {
    const filtered = items.filter((j) => !j.verified && j.approved);
    const ids = filtered.map((i) => i.id);
    const params = {
      ids,
      scopePrefix,
      employeeId: activeEmployee.id,
    };

    const action = await dispatch(verifyControllersVisits(params));
    if (!action.error) {
      loadImage();
      dispatch(loadPage());
    }
  };

  const shouldAddToChangedEvents = (changedRecord) => {
    const originalRecord = items.filter((i) => i.id === changedRecord.id)[0];
    const isEqual = _.isEqual(originalRecord, changedRecord);
    const changedArr = isEqual
      ? changedVisits.filter((i) => i.id !== changedRecord.id)
      : [
          ...changedVisits.filter((i) => i.id !== changedRecord.id),
          changedRecord,
        ];
    setChangedVisits(changedArr);
  };

  const onShowTransferClick = (record) => {
    setVisible(true);
    setFormType("transfer");
  };
  const pageStyle = `
  @media all {
  .page-break {
    display: none;
  }
}

@media print {
  html, body {
    height: initial !important;
    overflow: initial !important;
    -webkit-print-color-adjust: exact;
    direction: rtl;
  }
}

@media print {
  .page-break {
    margin-top: 1rem;
    display: block;
    page-break-before: auto
  }
}

@page {
   margin: 10mm;
  size: auto;
 }`;

  const onPrint = useReactToPrint({
    pageStyle: pageStyle,
    content: () => componentRef.current,
  });

  const hide = () => {
    setVisible(false);
    setFormType(null);
  };

  const onNextMonth = () => {
    dispatch(
      setMonth(moment(activeMonth).add(1, "months").startOf("month").format())
    );
  };
  const onPreviousMonth = () => {
    dispatch(
      setMonth(
        moment(activeMonth).subtract(1, "months").startOf("month").format()
      )
    );
  };

  const onSubmit = async () => {
    const isAdmin = location.pathname.includes("admin");
    const params = {
      branchId,
      admin: isAdmin,
      employeeId: activeEmployee.id,
      events: changedVisits,
    };
    const action = await dispatch(updateHouseVisits(params));
    if (!action.error) {
      setChangedVisits([]);
      dispatch(loadPage());
    }
  };

  const columns = getColumns(
    tableItems,
    setTableItems,
    shouldAddToChangedEvents,
    intl,
    dispatch,
    organization,
    userBranches,
    loadImage,
    getHouseVisit,
    deleteRecord,
    loadPage,
    setVisible,
    setMode,
    setFormType,
    location,
    showLocalId
  );

  const vComponents = useMemo(() => {
    return VList({
      height: window.innerHeight - 446,
      resetTopWhenDataChange: false,
    });
  }, []);

  const rowClassName = (record) => {
    const visitDate = moment(record.date);
    const startOfCurrentMonth = moment(activeMonth).startOf("month");
    return visitDate.isBefore(startOfCurrentMonth) ? "old-visit-row" : "";
  };

  return (
    <EmployeeHouseVisitsWrapper>
      {contextHolder}
      <Surface>
        <Header
          currentMonth={activeMonth}
          onNextMonth={onNextMonth}
          onPreviousMonth={onPreviousMonth}
          visits={filteredItems}
          onShowTransferClick={onShowTransferClick}
          onPrint={onPrint}
          branchId={branchId}
          onPrintAllVisits={onPrintAllVisits}
          onVerifyVisits={onVerifyVisits}
          type={type}
          filter={filter}
          setFilter={setFilter}
          showLocalId={showLocalId}
          setShowLocalId={setShowLocalId}
        />
        <StyledTable
          id={"visitsTableRef"}
          dataSource={filteredItems}
          columns={columns}
          rowKey={(record) => record.id}
          scroll={{
            y: window.innerHeight - 446,
          }}
          components={vComponents}
          loading={loading}
          pagination={false}
          rowClassName={rowClassName}
        />
      </Surface>
      <Modal
        closable={false}
        maskClosable={false}
        footer={null}
        width={"1000px"}
        height={"90%"}
        style={{ top: 20 }}
        styles={{
          body: {
            padding: 0,
          },
        }}
        title={null}
        open={visible}
        onCancel={hide}
        destroyOnClose={true}
      >
        {formType === "houseVisit" ? (
          <HouseVisit
            hide={hide}
            activeHouseVisit={activeHouseVisit}
            mode={mode}
            loadPage={loadPage}
          />
        ) : (
          <TransferHouseVisits
            hide={hide}
            branchId={branchId}
            activeEmployee={activeEmployee}
          />
        )}
      </Modal>
      <FloatingSubmitButton
        show={changedVisits.length > 0}
        saveLoading={false}
        disabled={false}
        onSubmit={onSubmit}
        saveText={intl.formatMessage({
          id: "app.actions.saveChanges",
        })}
      />
      <div
        style={{
          overflow: "hidden",
          height: 0,
          width: 0,
          position: "absolute",
        }}
      >
        <div ref={componentRef}>
          <Title level={2} style={{ margin: 0 }}>
            {`${intl.formatMessage({
              id: "app.house_visits.table.house_visits_title",
            })}: ${moment(activeMonth).format("MM/YYYY")}`}
          </Title>
          <Title level={4} style={{ margin: 0 }}>
            {`עבור: ${getFullName(
              activeEmployee.surename,
              activeEmployee.first_name
            )}`}
          </Title>
          <MonthlyPlanningTable visits={items} />
        </div>
      </div>
      <div
        style={{
          overflow: "hidden",
          height: 0,
          width: 0,
        }}
      >
        <div ref={houseVisitRef}>
          <PrintVersion />
        </div>
      </div>
    </EmployeeHouseVisitsWrapper>
  );
});

export default VisitsTable;
